import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import GriStandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import GriFormComponent from "components/customizedStandardsComponent/GriFormComponent";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import { COUNTRIES_COMPLIANCE_FORM } from "data/forms/griFormSchema";
import {
  getAnnexure9,
  FEDRAL_LAW_REGULATION_SPAIN,
  PROVISIONAL_TERRITORY_SPAIN,
  FEDRAL_PROVINCIAL_REQ_SPAIN,
} from "data/standards/griStandards";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import { CANADA_SPECIFIC_COMPLIANCE_FORM } from "data/forms/griFormSchema";
import { customItems } from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";
import DynamicDropdownForm, {
  ReusableInputDropdown,
} from "components/customizedStandardsComponent/ReusableSelect";
import {
  ENVIRONMENTAL_ACTS,
  CANADIAN_ENVIRONMENTAL_LEGISLATION,
} from "data/dropdowns/countryDropdown";
import PivotSelectForm from "components/customizedStandardsComponent/PivotSelectForm";
import DropdownWithChecker from "components/customizedStandardsComponent/DropdownWithChecker";

const Spain = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const steps = [
    "Select Federal Law / Regulation",
    "Select Provincial and Territorial Law / Regulation",
    "Select Federal & Provincial Compliance Requirements",
    "Spain-Specific Compliance Form",
    "Select GRI Standards",
    "Download Gap Form",
    "Upload Gap Form",
    "Download Review Reports",
  ];

  // useEffect(() => {
  //     if (activeStep === 4 && prevStep === 3) {
  //         setShowReviewPopup(true);
  //     }
  // }, [activeStep, prevStep]);

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
    console.log("Standards saved:", selectedStandards);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(5);
    setShowReviewPopup(false);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StandardsSelector
            standards={FEDRAL_LAW_REGULATION_SPAIN}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Select Federal Law/Regulation from the options below"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );

      case 1:
        return (
          <StandardsSelector
            standards={PROVISIONAL_TERRITORY_SPAIN}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Select Provincial and Territorial(Law/Regulation) from the options below"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 2:
        return (
          <StandardsSelector
            standards={FEDRAL_PROVINCIAL_REQ_SPAIN}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Select Federal, Provincial and Territorial(Law/Regulation) ESHS requirements from the options below"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 3:
        return (
          <ReusableFormComponent
            key="form-step-2"
            {...COUNTRIES_COMPLIANCE_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 4:
        return (
          <StandardsSelector
            standards={getAnnexure9("Spain")}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title=""
            showReportCountdownButton={true}
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 5:
        return (
          <DownloadPostReviewGapForm
            key="gap-download-step-6"
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );

      case 6:
        return (
          <UploadPostReviewGapForm
            key="gap-upload-step-7"
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );

      case 7:
        return (
          <DownloadPostReviewReports
            key="post-review-download-step-8"
            onPrevious={handleBack}
            showReportCountdownButton={true}
            standardName="Country Specific"
          />
        );

      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg4_2"
                title={"Country Specific ESG Compliance"}
                activeStep={activeStep}
                onBack={handleBack}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          ></Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -1,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default Spain;
